<template>
    <div v-if="loaded">
        <card-header title="Edit Event" icon="fa-pencil" :subheader="trip.title + ': Day ' + day.number + ': ' + dayDate"/>

        <entity-tabs v-model="tab" main-label="Event" main-icon="fa-pencil" show-contacts :contact-count="contactCount" :task-count="taskCount" :task-completed-count="taskCompleted" :note-count="noteCount"/>

        <card-toolbar v-show="tab === 'main'">
            <button @click="$openCard('new-event-document', {...props}, card)"><i class="fas fa-plus mr-2"/>New Document</button>
        </card-toolbar>
        <card-body v-if="notFound || deleteError">
            <card-list>
                <container-list-item padded v-if="notFound">
                    <i class="fas fa-exclamation-triangle mr-2"></i>This event no longer exists. It may have been deleted.
                </container-list-item>
                <container-list-item padded v-if="deleteError">
                    <i class="fas fa-exclamation-triangle mr-2"></i><strong>Unable to delete.</strong> {{ deleteError }}
                    <ul class="mt-2" v-if="deleteBlockers.length">
                        <li v-for="r in deleteBlockers" :key="r.registration_id">
                            <a @click="$openCard('registration', {registrationId: r.registration_id}, card)">{{ r.last_name }}, {{ r.first_name }} (registration {{ r.registration_id }})</a>
                        </li>
                    </ul>
                </container-list-item>
            </card-list>
        </card-body>
        <card-body v-if="event && !notFound">
            <entity-contacts-pane v-show="tab === 'contacts'" :card="card" :child="child" entity-type="trip-events" :entity-id="props.eventId" @count="contactCount = $event"/>
            <entity-tasks-pane v-show="tab === 'tasks'" :card="card" :child="child" entity-type="trip-events" :entity-id="props.eventId" @count="taskCount = $event" @completed="taskCompleted = $event"/>
            <entity-notes-pane v-show="tab === 'notes'" :card="card" :child="child" entity-type="trip-events" :entity-id="props.eventId" @count="noteCount = $event"/>
            <div v-show="tab === 'main'">
            <event-form :event="event" :tripId="trip.id" :card="card" :child="child"/>
            </div>
        </card-body>

        <card-footer class="pt-3 pb-3" v-if="!notFound" v-show="tab === 'main'">
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning is-outlined" size="is-small" @click="deleteEvent"><i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Delete Event?</span></b-button>
                </div>
                <div class="column has-text-right">
                    <b-button type="is-primary" size="is-small" :disabled="!event.title || !event.location || !event.start_date || !event.start_time || !event.start_tz || !event.end_date || !event.end_time || !event.end_tz" @click="submit"><i class="fas fa-check mr-2"></i>Save Event</b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardBody from "../../TIER/components/CardBody";
    import CardFooter from "../../TIER/components/CardFooter";
    import EventForm from "./EventForm";
    import CardList from "../../TIER/components/CardList";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import async from "async";
    import {client as http} from "../../http_client";
    import moment from "moment-timezone";
    import CardToolbar from "../../TIER/components/CardToolbar.vue";
    import EntityTabs from "@/components/EntityTabs";
    import EntityTasksPane from "@/components/EntityTasksPane";
    import EntityContactsPane from "@/components/EntityContactsPane";
    import EntityNotesPane from "@/components/EntityNotesPane";

    export default {
        components: {EntityContactsPane, EntityNotesPane, EntityTasksPane, EntityTabs, CardToolbar, ContainerListItem, SubheaderListItem, CardList, EventForm, CardFooter, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data: function() {
            return {
                event: null,
                day: {},
                trip: {},
                confirmDelete: false,
                dirty: null,
                loaded: false,
                notFound: false,
                deleteError: null,
                deleteBlockers: [],
                tab: 'main',
                contactCount: 0,
                taskCount: 0,
                taskCompleted: 0,
                noteCount: 0
            };
        },
        computed: {
            dayDate: function() {
                return moment(this.day.day_on).format('dddd, MMMM Do, YYYY');
            }
        },
        methods: {
            loadEvent: function() {
                this.$emit('loading', true);
                this.dirty = null;

                async.series([
                    // Get event
                    (cb) => {
                        http.get('/api/trips/' + this.props.tripId + '/days/' + this.props.dayId + '/events/' + this.props.eventId, {force: true}).then(response => {
                            this.event = response.data;
                            cb();
                        }).catch(err => {
                            this.notFound = true;
                            cb();
                        });
                    },
                    // Get day
                    (cb) => {
                        http.get('/api/days/' + this.props.dayId, {force: true}).then(response => {
                            this.day = response.data;
                            this.$emit('title', this.day.title);
                            cb();
                        }).catch(cb);
                    },
                    // Get trip
                    (cb) => {
                        http.get('/api/trips/' + this.day.trip_id, {force: true}).then(response => {
                            this.trip = response.data;
                            cb();
                        }).catch(cb);
                    }
                ], err => {
                    if (err) this.notFound = true;
                    this.$emit('loading', false);
                    this.loaded = true;
                });
            },
            submit() {
                this.$emit('loading', true);
                http.put('/api/trips/' + this.props.tripId + '/days/' + this.props.dayId + '/events/' + this.props.eventId, this.event, {force: true}).then(response => {
                    this.$reloadCard('tasks');
                    this.$reloadCard('day', {dayId: this.props.dayId});
                    this.$reloadCard('days', {tripId: this.props.tripId});
                    this.$reloadCard('trip', {tripId: this.props.tripId});
                    this.$reloadCard('trips');
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(err => {

                });
            },
            deleteEvent() {
                if (this.confirmDelete) {
                    this.$emit('loading', true);
                    this.deleteError = null;
                    this.deleteBlockers = [];

                    http.delete('/api/trips/' + this.props.tripId + '/days/' + this.props.dayId + '/events/' + this.props.eventId).then(response => {
                        this.$reloadCard('tasks');
                        this.$reloadCard('days', {tripId: this.props.tripId});
                        this.$saveAlert(false);
                        this.$closeCard(this.card);
                    }).catch(err => {
                        if (err.response && err.response.status === 409 && err.response.data) {
                            this.deleteError = err.response.data.message;
                            this.deleteBlockers = err.response.data.registrations || [];
                        } else {
                            this.deleteError = 'Something went wrong while deleting this event. Please try again.';
                        }
                        this.$emit('loading', false);
                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            },
            reload() {
                this.$emit('loading', true);

                http.get('/api/trips/' + this.props.tripId + '/days/' + this.props.dayId + '/events/' + this.props.eventId, {force: true}).then(response => {
                    this.event.documents = response.data.documents;
                    this.$emit('loading', false);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            }
        },
        watch: {
            'props.eventId': function() {
                this.dirty = null;
                this.loadEvent();
            },
            'event': {
                deep: true,
                handler: function() {
                    this.dirty = (this.dirty !== null);
                    if (!this.$TIER.saveAlert.active && this.dirty)
                        this.$saveAlert(true, 'Are you done editing this event? Unsaved changes will be lost.');
                }
            }
        },
        mounted() {
            this.loadEvent();
        }
    };
</script>

<style scoped>

</style>
